import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Inbox, CalendarCheck, Layers } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Admin() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [regs, setRegs] = useState([]);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    if (checked && (!user || user.role !== "admin")) navigate("/login");
  }, [checked, user, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
      api.get("/admin/enquiries").then(({ data }) => setEnquiries(data.enquiries)).catch(() => {});
      api.get("/admin/workshop-registrations").then(({ data }) => setRegs(data.registrations)).catch(() => {});
    }
  }, [user]);

  if (!checked || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-widest text-graphite">Checking access…</p>
      </div>
    );
  }

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-page">
      <header className="sticky top-0 z-40 bg-emerald text-paper">
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="bg-white rounded-md p-1 flex items-center justify-center">
                <img src="/fluenex-symbol.svg" alt="" aria-hidden="true" className="h-6 w-auto object-contain" />
              </span>
              <span className="font-display font-bold text-base tracking-tight text-white">Fluenex <span className="font-normal">Academy</span></span>
            </Link>
            <span className="font-mono text-[10px] uppercase tracking-widest text-mint border-l border-white/20 pl-4">Leads Dashboard</span>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 font-sans text-sm font-semibold rounded-full bg-terracotta text-white px-5 py-2.5 hover:bg-terracottahover transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="w-full mx-auto px-4 md:px-8 lg:px-12 py-10">
        <h1 className="font-display font-semibold tracking-tight text-4xl md:text-6xl leading-[1.0]">Enquiries & registrations</h1>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Stat icon={<Inbox size={20} />} label="Total enquiries" value={stats?.enquiries ?? "—"} tone="emerald" testId="stat-enquiries" />
          <Stat icon={<CalendarCheck size={20} />} label="Workshop signups" value={stats?.workshop_registrations ?? "—"} tone="terracotta" testId="stat-workshops" />
          <Stat icon={<Layers size={20} />} label="Live courses" value={stats?.courses ?? "—"} tone="surface" testId="stat-courses" />
        </div>

        <div className="mt-10 flex gap-2.5">
          <TabBtn active={tab === "enquiries"} onClick={() => setTab("enquiries")} testId="tab-enquiries">Enquiries ({enquiries.length})</TabBtn>
          <TabBtn active={tab === "workshops"} onClick={() => setTab("workshops")} testId="tab-workshops">Workshop registrations ({regs.length})</TabBtn>
        </div>

        <div className="mt-6 rounded-lg bg-surface border border-line overflow-x-auto shadow-sm">
          {tab === "enquiries" ? (
            <Table testId="enquiries-table" head={["When", "Name", "Contact", "Course", "Source", "Message"]}
              rows={enquiries.map((e) => [fmt(e.created_at), e.name, `${e.email} · ${e.phone}`, e.course_title || "General", e.source || "—", e.message || "—"])}
              empty="No enquiries yet. They'll appear here the moment someone enquires." />
          ) : (
            <Table testId="workshops-table" head={["When", "Name", "Contact", "Workshop"]}
              rows={regs.map((r) => [fmt(r.created_at), r.name, `${r.email} · ${r.phone}`, r.workshop_title || r.workshop_slug])}
              empty="No workshop registrations yet." />
          )}
        </div>
      </main>
    </div>
  );
}

const toneCls = { emerald: "bg-emerald text-paper", terracotta: "bg-terracotta text-white", surface: "bg-surface border border-line text-ink" };
const toneVal = { emerald: "text-amber", terracotta: "text-white", surface: "text-emerald" };

function Stat({ icon, label, value, tone, testId }) {
  return (
    <div className={`rounded-lg p-6 shadow-sm ${toneCls[tone]}`} data-testid={testId}>
      <div className={`flex items-center gap-2 ${tone === "surface" ? "text-terracotta" : "text-paper/80"}`}>{icon}<span className="font-mono text-[10px] uppercase tracking-widest">{label}</span></div>
      <p className={`font-display font-semibold text-5xl mt-3 ${toneVal[tone]}`}>{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`font-sans text-sm font-medium rounded-full px-5 py-2.5 border-2 transition-colors ${active ? "bg-emerald text-paper border-emerald" : "border-line text-emerald hover:border-emerald"}`}>
      {children}
    </button>
  );
}

function Table({ head, rows, empty, testId }) {
  if (!rows.length) {
    return <p className="font-mono text-xs uppercase tracking-widest text-graphite p-10 text-center">{empty}</p>;
  }
  return (
    <table className="w-full text-left" data-testid={testId}>
      <thead>
        <tr className="border-b border-line bg-stone">
          {head.map((h) => (<th key={h} className="font-mono text-[10px] uppercase tracking-widest text-graphite px-4 py-3 whitespace-nowrap">{h}</th>))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-line last:border-b-0 align-top hover:bg-stone/50 transition-colors">
            {r.map((cell, j) => (<td key={j} className="px-4 py-4 text-sm max-w-xs">{cell}</td>))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
